/**
 * Zustand store for notifications
 */

import { create } from 'zustand';
import axios from 'axios';
import { getApiUrl } from '../utils/config';

const useNotificationsStore = create((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
  lastFetched: null,
  
  // Fetch notifications
  fetchNotifications: async (unreadOnly = false, limit = 50, cursor = null) => {
    set({ loading: true, error: null });
    try {
      const params = new URLSearchParams({
        unread_only: unreadOnly.toString(),
        limit: limit.toString(),
      });
      if (cursor) {
        params.append('cursor', cursor);
      }
      
      const response = await axios.get(getApiUrl(`/notifications?${params.toString()}`));
      const { notifications, unread_count } = response.data;
      
      set({
        notifications: notifications || [],
        unreadCount: unread_count || 0,
        loading: false,
        lastFetched: new Date(),
      });
      
      return { notifications, unreadCount: unread_count || 0, nextCursor: response.data.next_cursor };
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },
  
  // Mark notification as read
  markRead: async (notificationId) => {
    try {
      await axios.post(getApiUrl(`/notifications/${notificationId}/read`));
      
      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      throw error;
    }
  },
  
  // Mark all notifications as read
  markAllRead: async () => {
    try {
      await axios.post(getApiUrl('/notifications/read-all'));
      
      set((state) => ({
        notifications: state.notifications.map((n) => ({
          ...n,
          read_at: n.read_at || new Date().toISOString(),
        })),
        unreadCount: 0,
      }));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      throw error;
    }
  },
  
  // Add notification (for real-time updates)
  addNotification: (notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + 1,
    }));
  },
  
  // Update unread count
  updateUnreadCount: (count) => {
    set({ unreadCount: count });
  },
  
  // Clear notifications
  clearNotifications: () => {
    set({ notifications: [], unreadCount: 0 });
  },
}));

export default useNotificationsStore;

